import AddNote from 'actions/composer/add_note';
import AddTask from 'actions/composition/add_task';
import AssignConversation from 'actions/conversation/assign_conversation';
import CloseConversation from 'actions/conversation/close_conversation';
import CreateChatComposition from 'actions/composition/create_chat_composition';
import CreateEmailComposition from 'actions/composition/create_email_composition';
import CreateNoteComposition from 'actions/composition/create_note_composition';
import CreateSmsComposition from 'actions/composition/create_sms_composition';
import CreateTaskComposition from 'actions/composition/create_task_composition';
import FetchCommentsAndFollowersForTask from 'actions/task/fetch_comments_and_followers_for_task';
import GetNextConversation from 'actions/conversation/get_next_conversation';
import GoAway from 'actions/agent_status/go_away';
import HighlightGroupedMessages from 'actions/composition/highlight_grouped_messages';
import InsertSelectedAnswer from 'actions/answers/insert_selected_answer';
import LoadCustomer from 'actions/customer/load_customer';
import NavigateToConversation from 'actions/conversation/navigate_to_conversation';
import NavigateToCustomerProfile from 'actions/customer/navigate_to_customer_profile';
import OpenAnswerPanel from 'actions/answers/open_answer_panel';
import OpenHome from 'actions/inbox/open_home';
import OpenTeamInbox from 'actions/inbox/open_team_inbox';
import ReopenConversation from 'actions/conversation/reopen_conversation';
import ReturnFromAway from 'actions/agent_status/return_from_away';
import ScrollToConversationItem from 'actions/conversation_item/scroll_to_conversation_item';
import Search from 'actions/search/search';
import SearchAnswers from 'actions/knowledge_base/search_answers';
import SearchQuery from 'models/search/search_query';
import SetSearchText from 'actions/answers/set_search_text';
import StartAnswerSearch from 'actions/answers/start_answer_search';
import SwitchCurrentConversation from 'actions/customer/switch_current_conversation';
import SwitchInboxSort from 'actions/inbox/switch_inbox_sort';
import SwitchToInbox from 'actions/inbox/switch_to_inbox';
import UpdateComposition from 'actions/composition/update_composition';
import UpdateConversationTopicIds from 'actions/conversation/update_conversation_topic_ids';
import UpdateTaskCompositionAssignee from 'actions/composition/update_task_composition_assignee';

/**
 * The set of actions reachable through the action bridge, keyed by the stable
 * name external callers use. Exposure is opt-in per action: an action that is
 * not listed here cannot be run through the bridge.
 *
 * Entries are `{ action, adapt }`. `adapt` is only needed for actions whose
 * `run` takes a positional scalar rather than an attributes object — callers
 * always send an object, so `adapt` maps that object onto the argument the
 * action expects.
 *
 * Three rules govern what belongs here:
 *
 * 1. Nothing reaches the customer. Every action that delivers to them —
 *    `send_email`, `send_chat_message`, `send_sms`, `send_fb_message`,
 *    `send_custom_channel_message`, `send_conversation_message` — is
 *    deliberately absent, and the `draft*` entries only open a composition for
 *    the agent to review and send themselves.
 *
 *    Notes and tasks are the exception, and are created outright rather than
 *    drafted: neither is customer-visible. A note is an internal annotation on
 *    the conversation and a task is internal work, so the agent seeing one
 *    appear is the intended outcome. Everything customer-facing still stops at a
 *    draft.
 * 2. Plain-data parameters at the boundary. Callers send serialized payloads, so
 *    an action wanting a domain model needs `adapt` to build one — see
 *    `search.all`. Actions whose model has to be *looked up* rather than
 *    constructed (`select_answer` wants an existing Snippet,
 *    `translate_composition` an existing Composition) are omitted until they
 *    have an id-to-model resolver.
 * 3. No gateway observers. Those handle inbound events and are not commands.
 *
 * Also intentionally absent: live phone-call control, secure data / credit
 * card capture, compliance and redaction, profile merges, imitation, and
 * user/org administration.
 */
const ACTION_REGISTRY = {
  // Inbox and navigation
  'inbox.openHome': { action: OpenHome },
  'inbox.openTeamInbox': { action: OpenTeamInbox },
  'inbox.switchToInbox': { action: SwitchToInbox },
  'inbox.switchSort': { action: SwitchInboxSort, adapt: ({ sortOrder }) => sortOrder },

  // Universal search. The caller supplies `id` so it can read the result back
  // out of the `universalSearchResults` store once the fetch settles.
  //
  // Search takes a SearchQuery model, not a plain object — its DTO converter
  // calls `query.toJs()`. Building the model here is the point of `adapt`: it
  // keeps the boundary plain-data for callers while the app supplies the model.
  'search.all': {
    action: Search,
    adapt: ({ id, text, from, size, sort, requestedAt }) => ({
      id,
      query: new SearchQuery({ text, from, size, sort }),
      requestedAt: requestedAt || new Date().toISOString(),
    }),
  },

  // Customer
  'customer.load': { action: LoadCustomer, adapt: ({ customerId }) => customerId },
  'customer.openProfile': { action: NavigateToCustomerProfile },
  'customer.switchConversation': { action: SwitchCurrentConversation },

  // Conversation
  'conversation.navigateTo': { action: NavigateToConversation },
  'conversation.assign': { action: AssignConversation },
  'conversation.close': { action: CloseConversation },
  'conversation.reopen': { action: ReopenConversation },
  'conversation.updateTopics': { action: UpdateConversationTopicIds },
  'conversation.getNext': { action: GetNextConversation },
  'conversationItem.scrollTo': { action: ScrollToConversationItem },

  // Drafting. These require the agent to already be on a customer view —
  // CreateComposition returns early unless the current location is a
  // CustomerView — so pair them with `customer.openProfile`.
  'composition.draftEmail': { action: CreateEmailComposition },
  'composition.draftChat': { action: CreateChatComposition },
  'composition.draftSms': { action: CreateSmsComposition },
  'composition.draftNote': { action: CreateNoteComposition },
  'composition.draftTask': { action: CreateTaskComposition },
  'composition.update': { action: UpdateComposition },

  // Notes are created outright, not drafted — a note is internal and never shown
  // to the customer. Pair with `composition.draftNote` to get a compositionId,
  // then submit here. This is the `actions/composer` wrapper rather than the raw
  // `actions/composition` submit, because it is what the note composer itself
  // uses: it converts @-mentioned agent ids into real mentions (so they get
  // notified) and merges the passed content over what is already on the
  // composition.
  'composer.addNote': { action: AddNote },

  // Answers and knowledge base
  'answers.search': { action: SearchAnswers },
  'answers.setSearchText': { action: SetSearchText },
  'answers.startSearch': { action: StartAnswerSearch },
  'answers.openPanel': { action: OpenAnswerPanel },
  'answers.insertSelected': { action: InsertSelectedAnswer },

  // Tasks. Creating one is a three-step flow, because AddTask submits whatever
  // is already on the composition and validates its assignee:
  //   1. `composition.draftTask`            open the task composition
  //   2. `composition.setTaskAssignee`      required — AddTask rejects a task
  //                                         whose assigned inbox is disabled,
  //                                         and the assignee lives on the
  //                                         composition, not on the submit call
  //   3. `composition.addTask`              create it
  //
  // Allowed despite being a SubmitComposition descendant: a task is internal
  // work, never shown to the customer, and it is not even added to the
  // conversation timeline — AddTask overrides `addItemToConversation` to a no-op
  // and posts via `gateways.task.add`.
  'composition.setTaskAssignee': { action: UpdateTaskCompositionAssignee },
  'composition.addTask': { action: AddTask },
  'task.fetchComments': { action: FetchCommentsAndFollowersForTask },

  // Agent status. These change whether the agent receives routed work.
  'agentStatus.goAway': { action: GoAway },
  'agentStatus.returnFromAway': { action: ReturnFromAway },

  // Draw the agent's attention to something already on screen
  'ui.highlightGroupedMessages': { action: HighlightGroupedMessages },
};

export default ACTION_REGISTRY;
